import PageWrapper from "../../components/PageWrapper";
import { useTranslation } from "react-i18next";
import PDFViewer from "../../components/PDF";
import sendIcon from "../../assets/send_icon.png";
import { useEffect, useMemo, useRef, useState } from "react";
import SectionDivider from "../../components/SectionDivider";
import CustomButton from "../../components/CustomButton";
import editIcon from "../../assets/edit_icon.png";
import classes3 from "../../components/PDFUploader.module.css";
import Loader, { LOADER_SIZES } from "../../components/Loader";
import Modal from "../../components/Modal";
import deleteIcon from "../../assets/circled_cancel.svg";
import uploadIcon from "../../assets/upload_icon.png";
import previewIcon from "../../assets/preview.svg";
import closeIcon from "../../assets/close_icon.png";
import { DoneIcon, WarningIcon } from "../../assets/registtyIcons/icons";
import { useNavigate, useParams } from "react-router-dom";
import trashIcon from "../../assets/delete_Icon.png";
import ErrorBox from "../../components/ErrorBox";
import CustomInput from "../../components/CustomInput";
import saveIcon from "../../assets/save_icon.png";
import InformationBox from "../../components/InformationBox";
import CustomInputWithUnit from "../../components/CustomInputWithUnit";
import AreYouSureModal from "../../components/AreYouSureModal";
import PaymentStatusBadge from "./PaymentStatusBadge";
import axiosClient from "../../services/axiosClient";
import { getFileType, sortByCustomOrder } from "../../utils/files";
import FilesUpload from "../../components/FileUpload";

const INVOICE_FILES_TYPES = [
  "invoice",
  "supplierCertificate",
  "articlesOfAssociation",
];
const orderedKeys = [
  "MansurTransactionRequest",
  "MansurBankForeignCurrencyTradingWindowRequest",
  "MansurBankPromise",
  "BaghdadTransactionRequest",
  "BaghdadPaymentDetails",
  "BaghdadConfirmation",
  "BaghdadEntryRequest",
  "BaghdadPromise",
  "BankPledge",
];

const invoiceOrderFiles = [
  "invoice",
  "supplierCertificate",
  "articlesOfAssociation",
  "MansurTransactionRequest",
  "BaghdadTransactionRequest",
];

const ViewInvoicePaymentsPage = () => {
  const { t } = useTranslation("general");
  const [paymentDocs, setPaymentDocs] = useState([]);
  const [invoiceDocs, setInvoiceDocs] = useState([]);
  const [payment, setPayment] = useState({});
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [paymentEditableFields, setPaymentEditableFields] = useState({});
  const [sentConfirmationModal, setSentConfirmationModal] = useState(false);
  const { id, paymentId } = useParams();
  const [updateInvoicePaymentLoading, setUpdateInvoicePaymentLoading] =
    useState(false);
  const [updateInvoicePaymentError, setUpdateInvoicePaymentError] =
    useState("");
  const [isSendingToBank, setIsSendingToBank] = useState(false);
  const [sendingToBankError, setSendingToBankError] = useState("");
  const [loadingPayment, setLoadingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletingError, setDeletingError] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isFetchingInvoiceFiles, setIsFetchingInvoiceFiles] = useState(false);
  const [showDocuments, setShowDocuments] = useState(false);

  const [invoiceId, setInvoiceId] = useState(null);
  const navigate = useNavigate();

  const fetchPayment = async () => {
    try {
      setLoadingPayment(true);
      setPaymentError("");
      const response = await axiosClient.get(
        `/multiCInvoicePaymentDetails/${paymentId}`,
      );
      setPayment(response.data);
      setInvoiceId(response.data.multiContainerInvoice.id);

      setPaymentEditableFields({
        currentPaid: response.data.amount,
        paymentDate: response.data.date.split("T")[0],
      });

      setPaymentDocs(response.data.MCIPaymentDocs);
    } catch (error) {
      setPaymentError(t("errorGettingInvoicePayment"));
    } finally {
      setLoadingPayment(false);
    }
  };

  async function getInvoiceDocs() {
    try {
      setIsFetchingInvoiceFiles(true);

      const response = await axiosClient.get(
        `/multiContainerInvoiceWithoutC/invoiceMainFiles/${id}`,
      );

      if (response.status === 200) {
        setInvoiceDocs(
          response.data?.data?.documents.filter(
            (file) =>
              ![
                "BaghdadTransactionRequest",
                "MansurTransactionRequest",
                "supplierCertificate",
                "articleOfAssociation",
              ].includes(file.type),
          ),
        );
      }
    } catch (error) {
      console.error(error);
      // setError("errorGettingInvoicePayments");
    } finally {
      setIsFetchingInvoiceFiles(false);
    }
  }

  const updateInvoicePayment = async () => {
    try {
      setUpdateInvoicePaymentLoading(true);
      setUpdateInvoicePaymentError("");
      await axiosClient.put(`/multiCInvoicePaymentDetails/${paymentId}`, {
        amount: paymentEditableFields.currentPaid,
        date: paymentEditableFields.paymentDate,
        multiContainerInvoiceId: payment.multiContainerInvoice.id,
        existingFileTypes: paymentDocs.map((doc) => doc.type),
      });
      fetchPayment();
      setUpdateInvoicePaymentLoading(false);
      setIsEditing(false);
    } catch (error) {
      console.log("error updating invoice payment", error);
      setUpdateInvoicePaymentLoading(false);
      setUpdateInvoicePaymentError(t("errorUpdatingInvoicePayment"));
    }
  };

  const handleDeletePayment = async () => {
    try {
      setIsDeleting(true);
      setDeletingError("");
      await axiosClient.delete(`/multiCInvoicePaymentDetails/${paymentId}`, {
        data: {
          invId: payment.multiContainerInvoice.id,
        },
      });
      navigate(`/bankApplications/${id}/payments`, {
        replace: true,
      });
    } catch (error) {
      console.log("error deleting invoice payment", error);
      setDeletingError(t("errorDeletingInvoicePayment"));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleUpdateFiles = async (files) => {
    setIsEditing(false);
    fetchPayment();
  };

  const handleSendToBank = async () => {
    try {
      setSendingToBankError("");
      setIsSendingToBank(true);
      await axiosClient.patch(
        `/multiCInvoicePaymentDetails/updateStatus/${paymentId}`,
        {
          status: "NOT_STARTED",
        },
      );
      setPayment({ ...payment, companyPaymentStatus: "NOT_STARTED" });
      setSentConfirmationModal(false);
      fetchPayment();
    } catch (error) {
      console.log("error updating invoice payment", error);
      setSendingToBankError(t("errorSendingToBank"));
    } finally {
      setIsSendingToBank(false);
    }
  };

  const filesTitles = useMemo(
    () => ({
      invoice: t("invoice"),
      supplierCertificate: t("supplierCertificate"),
      articlesOfAssociation: t("articlesOfAssociation"),
      BaghdadConfirmation: t("confirmDocument"),
      BaghdadEntryRequest: t("transactionRequest"),
      BaghdadPromise: t("declarationAndUndertaking"),
      BaghdadTransactionRequest: t("financialTransferRequestForm"),
      BankPledge: t("bankPledge"),
      MansurTransactionRequest: t("financialTransferRequestForm"),
      MansurBankForeignCurrencyTradingWindowRequest: t(
        "foreignCurrencyTradingWindowRequest",
      ),
      MansurBankPromise: t("promise"),
    }),
    [t],
  );

  useEffect(() => {
    fetchPayment();
    getInvoiceDocs();
  }, []);

  const canEditPayment =
    payment.companyPaymentStatus === "NOT_SENT" ||
    payment.companyPaymentStatus === "REJECTED" ||
    payment?.multiContainerInvoice?.bankStatus == "REJECTED";

  if (loadingPayment) {
    return (
      <PageWrapper title={t("paymentDetails")} withBackButton>
        <Loader size={LOADER_SIZES.LARGE} />
      </PageWrapper>
    );
  }

  if (paymentError) {
    return (
      <PageWrapper title={t("paymentDetails")} withBackButton>
        <ErrorBox>{t(paymentError)}</ErrorBox>
      </PageWrapper>
    );
  }

  const invoiceFiles = sortByCustomOrder(
    invoiceDocs,
    invoiceOrderFiles,
    "type",
  );
  const bankFiles = sortByCustomOrder(paymentDocs, orderedKeys, "type");

  const validateDocuments = (invoiceDocs, paymentsDocs) => {
    const requirements = {
      invoice: ["invoice"],
      payments: [
        "BaghdadEntryRequest",
        "BaghdadTransactionRequest",
        "BaghdadPromise",
      ],
    };

    // Check if every required invoice type exists in the invoiceDocs array
    const hasAllInvoices = requirements.invoice.every((type) =>
      invoiceDocs.some((doc) => doc.type === type),
    );

    // Check if every required payment type exists in the paymentsDocs array
    const hasAllPayments = requirements.payments.every((type) =>
      paymentsDocs.some((doc) => doc.type === type),
    );

    // Returns true only if BOTH arrays have all their respective required files
    return hasAllInvoices && hasAllPayments;
  };

  const isSendButtonDisabled = !validateDocuments(invoiceDocs, paymentDocs);

  return (
    <PageWrapper title={t("paymentDetails")} withBackButton>
      <div className="u-margin--top_700" />
      <SectionDivider title={t("statusDetails")} />
      <div className="o-column o-column--gap-size_500">
        <div className="o-row o-row--vertical-align_center">
          <div className="u-min-width-specific_200">{t("status")}:</div>
          <span className="u-width_full-width t-bold_bold">
            <PaymentStatusBadge
              status={
                payment.companyPaymentStatus === "NOT_STARTED"
                  ? "SENT"
                  : payment.companyPaymentStatus === "SENT_TO_POLICIES" &&
                      localStorage.getItem("organizationType") !== "BANK"
                    ? "PRIMARY_CHECK"
                    : payment.companyPaymentStatus
              }
            />
          </span>
        </div>
        {payment.companyPaymentStatus === "REJECTED" && (
          <div className="o-row o-row--vertical-align_center">
            <div className="u-min-width-specific_200">
              {t("declineReason")}:
            </div>
            <span
              className="u-width_full-width"
              style={{ whiteSpace: "pre-line" }}
            >
              {payment.notes ? payment.notes : t("noNotes")}
            </span>
          </div>
        )}
      </div>
      <SectionDivider title={t("paymentDetails")} />
      <div className="u-margin--top_500 o-column o-column--gap-size_500">
        <div className="o-row o-row--vertical-align_center">
          <div className="u-min-width-specific_200">{t("invoiceAmount")}:</div>
          <span className="u-width_full-width t-bold_bold">
            {payment.multiContainerInvoice?.amountForSupplier} USD
          </span>
        </div>
        <div className="o-row o-row--vertical-align_center">
          <div className="u-min-width-specific_200">{t("previousPaid")}:</div>
          <span className="u-width_full-width t-bold_bold">
            {payment?.multiContainerInvoice?.previouslyPaid} USD
          </span>
        </div>
        {isEditing && (
          <div className="o-row o-row--vertical-align_center">
            <div className="u-min-width-specific_200">{t("currentPaid")}:</div>
            <CustomInputWithUnit
              unit="USD"
              value={paymentEditableFields.currentPaid}
              onChange={(e) => {
                const value = Number(e.target.value);
                if (
                  value <= payment?.multiContainerInvoice?.amountForSupplier
                ) {
                  setPaymentEditableFields({
                    ...paymentEditableFields,
                    currentPaid: value,
                  });
                }
              }}
            />
          </div>
        )}
        {!isEditing && (
          <div className="o-row o-row--vertical-align_center">
            <div className="u-min-width-specific_200">{t("currentPaid")}:</div>
            <span className="u-width_full-width t-bold_bold">
              {payment?.amount?.toFixed(4)} USD
            </span>
          </div>
        )}
        <div className="o-row o-row--vertical-align_center">
          <div className="u-min-width-specific_200">{t("paidPercentage")}:</div>
          <span className="u-width_full-width t-bold_bold">
            {isEditing ? (
              <>
                {Math.round(
                  (paymentEditableFields.currentPaid /
                    payment?.multiContainerInvoice?.amountForSupplier) *
                    100,
                  4,
                )}
                %
              </>
            ) : (
              <>
                {Math.round(
                  (payment?.amount /
                    payment?.multiContainerInvoice?.amountForSupplier) *
                    100,
                  4,
                )}
                %
              </>
            )}
          </span>
        </div>
        {isEditing && (
          <div className="o-row o-row--vertical-align_center">
            <div className="u-min-width-specific_200">{t("paymentDate")}:</div>
            <CustomInput
              type="date"
              value={paymentEditableFields.paymentDate}
              onChange={(e) => {
                setPaymentEditableFields({
                  ...paymentEditableFields,
                  paymentDate: e.target.value,
                });
              }}
            />
          </div>
        )}
        {!isEditing && (
          <div className="o-row o-row--vertical-align_center">
            <div className="u-min-width-specific_200">{t("paymentDate")}:</div>
            <span className="u-width_full-width t-bold_bold">
              {payment?.date?.split("T")[0]}
            </span>
          </div>
        )}
      </div>
      <div className="u-margin--top_700" />

      {showDocuments ? (
        <>
          <SectionDivider title={t("invoiceAndReceverDocuments")} />
          {invoiceFiles?.length === 0 && (
            <InformationBox>{t("noDocuments")}</InformationBox>
          )}

          {invoiceFiles.length > 0 && (
            <div className="o-grid o-grid--auto-columns_xsmall o-grid--gap-size_700">
              {invoiceFiles.map((doc) => (
                <PDFViewer
                  key={doc?.id}
                  path={`${process.env.REACT_APP_URL}/multiContainerInvoiceWithoutC/getInvoiceFile/${doc?.id}`}
                  filename={filesTitles[doc?.type]}
                  fileType={getFileType(doc?.filePath)}
                  fileSubname={
                    [
                      "BaghdadTransactionRequest",
                      "MansurTransactionRequest",
                    ].includes(doc?.type)
                      ? `(${t("lastPayment")})`
                      : ""
                  }
                />
              ))}
            </div>
          )}

          <div className="u-margin--top_700" />
          <SectionDivider title={t("bankDocuments")} />
          {bankFiles?.length === 0 && (
            <InformationBox>{t("noDocuments")}</InformationBox>
          )}
          {bankFiles.length > 0 && (
            <div className="o-grid o-grid--auto-columns_xsmall o-grid--gap-size_700">
              {bankFiles.map((doc) => (
                <PDFViewer
                  key={doc?.id}
                  path={`${process.env.REACT_APP_URL}/multiCInvoicePaymentDetails/invoicePayment/docs/${doc?.id}`}
                  filename={filesTitles[doc?.type]}
                  fileType={getFileType(doc?.filePath)}
                />
              ))}
            </div>
          )}
        </>
      ) : null}

      {!showDocuments && (
        <CustomButton
          variant="blank"
          title={showDocuments ? t("hideDocuments") : t("showDocuments")}
          onClick={() => {
            setShowDocuments((prev) => !prev);
          }}
          icon={previewIcon}
          iconSize={24}
        />
      )}

      <div className="o-row o-row--horizontal-align_end">
        {isEditing && (
          <div className="u-margin--top_300">
            <CustomButton
              variant="blank"
              title={t("editDocuments")}
              onClick={() => setShowUploadModal(true)}
              icon={editIcon}
              iconSize={18}
            />
          </div>
        )}
      </div>
      {updateInvoicePaymentLoading && <Loader size={LOADER_SIZES.SMALL} />}
      {updateInvoicePaymentError && (
        <ErrorBox>{updateInvoicePaymentError}</ErrorBox>
      )}

      {payment?.companyPaymentStatus === "NOT_STARTED" && (
        <InformationBox>
          <div className="o-row o-row--gap-size_200 o-row--horizontal-align_end o-row--vertical-align_center">
            <p>{t("transactionHasBeenSentToBank")}</p>
            <DoneIcon width={25} height={25} />
          </div>
        </InformationBox>
      )}

      {!isEditing && canEditPayment && isSendButtonDisabled && (
        <InformationBox>
          <p>{t("pleaseUploadMandatoryFilesToSendToBank")}</p>
        </InformationBox>
      )}

      <div className="o-row o-row--gap-size_200 o-row--horizontal-align_end u-padding--bottom_500 u-margin--top_700">
        {isEditing && (
          <>
            <CustomButton
              variant="blank"
              title={t("cancel")}
              onClick={() => {
                setIsEditing(false);
                setUpdateInvoicePaymentError("");
              }}
              icon={closeIcon}
              iconSize={15}
            />
            <CustomButton
              title={t("delete")}
              variant="red"
              icon={trashIcon}
              onClick={() => setShowDeleteModal(true)}
              iconSize={15}
            />
            <CustomButton
              title={t("save")}
              icon={saveIcon}
              onClick={() => {
                updateInvoicePayment();
              }}
              iconSize={18}
            />
          </>
        )}

        {!isEditing && canEditPayment && (
          <>
            <CustomButton
              title={t("edit")}
              variant="blank"
              icon={editIcon}
              onClick={() => {
                setUpdateInvoicePaymentError("");
                setIsEditing(true);
                document.body.scrollIntoView({ behavior: "smooth" });
              }}
              iconSize={18}
            />
            <CustomButton
              title={t("sendToBank")}
              onClick={() => setSentConfirmationModal(true)}
              icon={sendIcon}
              iconSize={15}
              disabled={isSendButtonDisabled}
            />
          </>
        )}
      </div>

      {sentConfirmationModal && (
        <AreYouSureModal
          loading={isSendingToBank}
          error={sendingToBankError}
          confirmTitle={"send"}
          confirmIconType="whiteCheck"
          closeTitle={"cancel"}
          customMessage={"areYouSureYouWantToSendPaymentToBank"}
          confirm
          onConfirm={() => {
            handleSendToBank();
          }}
          onClose={() => {
            setSentConfirmationModal(false);
          }}
        />
      )}

      {showUploadModal && (
        <UploadApplicationsDocumentsModal
          bankName={payment?.applicationBankName.nameWithNoSpace}
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          onSave={(files) => {
            handleUpdateFiles({
              files,
            });
          }}
          title="uploadApplicationsDocuments"
          modalSize="medium"
          invoice={{
            files: {
              BaghdadEntryRequest: paymentDocs.find(
                (doc) => doc?.type === "BaghdadEntryRequest",
              ),
              BaghdadConfirmation: paymentDocs.find(
                (doc) => doc?.type === "BaghdadConfirmation",
              ),
              BaghdadPaymentDetails: paymentDocs.find(
                (doc) => doc?.type === "BaghdadPaymentDetails",
              ),
              BaghdadPromise: paymentDocs.find(
                (doc) => doc?.type === "BaghdadPromise",
              ),
              BaghdadTransactionRequest: paymentDocs.find(
                (doc) => doc?.type === "BaghdadTransactionRequest",
              ),
              BankPledge: paymentDocs.find((doc) => doc?.type === "BankPledge"),
              MansurTransactionRequest: paymentDocs.find(
                (doc) => doc?.type === "MansurTransactionRequest",
              ),
              MansurBankForeignCurrencyTradingWindowRequest: paymentDocs.find(
                (doc) =>
                  doc?.type === "MansurBankForeignCurrencyTradingWindowRequest",
              ),
              MansurBankPromise: paymentDocs.find(
                (doc) => doc?.type === "MansurBankPromise",
              ),
            },
          }}
          paymentId={paymentId}
          invoiceId={invoiceId}
        />
      )}
      {showDeleteModal && (
        <AreYouSureModal
          loading={isDeleting}
          error={deletingError}
          confirmTitle={"delete"}
          closeTitle={"cancel"}
          customMessage={"areYouSureYouWantToDeletePayment"}
          confirmIconType="whiteCheck"
          confirm
          onConfirm={() => {
            handleDeletePayment();
          }}
          onClose={() => {
            setShowDeleteModal(false);
          }}
        />
      )}
    </PageWrapper>
  );
};

function UploadApplicationsDocumentsModal({
  onClose,
  modalSize = "medium",
  invoice = {},
  invoiceId,
  onSave,
  paymentId,
  bankName,
}) {
  const { t } = useTranslation("general");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [updatingError, setUpdatingError] = useState("");
  const [isLoadingFiles, setIsLoadingFiles] = useState(true);
  const [files, setFiles] = useState({
    BaghdadConfirmation: null,
    BaghdadEntryRequest: null,
    BaghdadPaymentDetails: null,
    BaghdadPromise: null,
    BaghdadTransactionRequest: null,
    BankPledge: null,
    MansurTransactionRequest: null,
    MansurBankForeignCurrencyTradingWindowRequest: null,
    MansurBankPromise: null,
  });

  // Fetch existing files on component mount
  useEffect(() => {
    const fetchExistingFiles = async () => {
      try {
        setIsLoadingFiles(true);

        const invoiceFiles = invoice?.files || {};
        const fileMetadata = {};

        // Store file metadata without downloading
        for (const fileInfo in invoiceFiles) {
          const file = invoiceFiles[fileInfo];
          if (file === undefined) continue;

          const fileExtension = getFileType(file.filePath);
          fileMetadata[file.type] = {
            name: file.name || `${file.type}.${fileExtension}`,
            _isExisting: true,
            _id: file.id,
            _extension: fileExtension,
          };
        }

        setFiles((prev) => ({
          ...prev,
          ...fileMetadata,
        }));
      } catch (err) {
        console.error("Error fetching existing files:", err);
      } finally {
        setIsLoadingFiles(false);
      }
    };

    if (paymentId) {
      fetchExistingFiles();
    } else {
      setIsLoadingFiles(false);
    }
  }, [paymentId, invoice]);

  const handleClose = () => {
    setFiles({
      BaghdadConfirmation: null,
      BaghdadEntryRequest: null,
      BaghdadPaymentDetails: null,
      BaghdadPromise: null,
      BaghdadTransactionRequest: null,
      BankPledge: null,
      MansurTransactionRequest: null,
      MansurBankForeignCurrencyTradingWindowRequest: null,
      MansurBankPromise: null,
    });
    setUpdatingError("");
    onClose();
  };

  const handleFileChange = (file, fieldName) => {
    setFiles((prev) => ({
      ...prev,
      [fieldName]: file,
    }));

    if (updatingError) setUpdatingError("");
  };

  const handleDeleteFile = (fieldName) => {
    setFiles((prev) => ({
      ...prev,
      [fieldName]: null,
    }));
  };

  const handleSave = async () => {
    try {
      setIsSubmitting(true);
      setUpdatingError("");

      const formData = new FormData();

      const fileFields = [
        "BaghdadConfirmation",
        "BaghdadEntryRequest",
        "BaghdadPaymentDetails",
        "BaghdadPromise",
        "BaghdadTransactionRequest",
        "BankPledge",
        "MansurTransactionRequest",
        "MansurBankPromise",
        "MansurBankForeignCurrencyTradingWindowRequest",
      ];

      // Add all files to formData
      for (const field of fileFields) {
        const currentFile = files[field];

        if (currentFile) {
          if (currentFile._isExisting) {
            // Existing file - send empty string to keep it
            formData.append(field, "");
          } else {
            // New file - send the file
            formData.append(field, currentFile);
          }
        }
        // If null/undefined, don't append anything (file will be deleted)
      }

      formData.append("invoiceId", invoiceId);

      await axiosClient.put(
        `/multiCInvoicePaymentDetails/${paymentId}?invoiceId=${invoiceId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      onSave?.();
      handleClose();
    } catch (err) {
      console.error(err);
      setUpdatingError("errorUploadingPaymentFiles");
    } finally {
      setIsSubmitting(false);
    }
  };

  const error = updatingError;

  const renderBankComponent = () => {
    switch (bankName) {
      case "مصرفالمنصور":
        return (
          <MansurBankFiles
            files={files}
            onFileChange={handleFileChange}
            onDeleteFile={handleDeleteFile}
          />
        );

      case "مصرفبغداد":
        return (
          <BaghdadBankFiles
            files={files}
            onFileChange={handleFileChange}
            onDeleteFile={handleDeleteFile}
          />
        );

      default:
        return (
          <InformationBox>
            <p>{t("thisBankIsNotSupported")}</p>
          </InformationBox>
        );
    }
  };

  return (
    <Modal
      title={t("editTransactionDocuments")}
      onClose={handleClose}
      modalSize={modalSize}
      error={t(error)}
      footer={
        <>
          {isSubmitting && <Loader size={LOADER_SIZES.MEDIUM} />}
          <section className="o-row o-row--gap-size_300 o-row--horizontal-align_end">
            <CustomButton
              title={t("cancel")}
              onClick={handleClose}
              variant="blank"
              icon={closeIcon}
              iconSize={14}
              disabled={isSubmitting}
            />
            <CustomButton
              title={t("save")}
              onClick={handleSave}
              icon={saveIcon}
              iconSize={20}
              disabled={isSubmitting || isLoadingFiles}
            />
          </section>
        </>
      }
    >
      {error && <ErrorBox>{t(error)}</ErrorBox>}
      {isLoadingFiles ? (
        <Loader size={LOADER_SIZES.MEDIUM} />
      ) : (
        <div className="o-column o-column--gap-size_300">
          {renderBankComponent()}
        </div>
      )}
    </Modal>
  );
}

function BaghdadBankFiles({ files, onFileChange, onDeleteFile }) {
  const { t } = useTranslation("general");

  return (
    <div className="o-column o-column--gap-size_200">
      <InformationBox>
        <p>{t("uploadAllFilesSeperately")}</p>
      </InformationBox>

      <div className="o-column u-margin--top_200">
        <SectionDivider
          title={t("financialTransferRequestForm")}
          noMargin={true}
        />
        <FilesUpload
          file={files.BaghdadTransactionRequest}
          onFileChange={(file) =>
            onFileChange(file, "BaghdadTransactionRequest")
          }
          onDeleteFile={() => onDeleteFile("BaghdadTransactionRequest")}
          label={t("financialTransferRequestForm")}
          isMand={true}
        />
      </div>

      <div className="o-column u-margin--top_200">
        <SectionDivider title={t("transactionRequest")} noMargin={true} />
        <FilesUpload
          file={files.BaghdadEntryRequest}
          onFileChange={(file) => onFileChange(file, "BaghdadEntryRequest")}
          onDeleteFile={() => onDeleteFile("BaghdadEntryRequest")}
          label={t("transactionRequest")}
          isMand={true}
        />
      </div>

      <div className="o-column u-margin--top_200">
        <SectionDivider
          title={t("declarationAndUndertaking")}
          noMargin={true}
        />
        <FilesUpload
          file={files.BaghdadPromise}
          onFileChange={(file) => onFileChange(file, "BaghdadPromise")}
          onDeleteFile={() => onDeleteFile("BaghdadPromise")}
          label={t("declarationAndUndertaking")}
          isMand={true}
        />
      </div>

      <div className="o-column u-margin--top_200">
        <SectionDivider title={t("confirmDocument")} noMargin={true} />
        <FilesUpload
          file={files.BaghdadConfirmation}
          onFileChange={(file) => onFileChange(file, "BaghdadConfirmation")}
          onDeleteFile={() => onDeleteFile("BaghdadConfirmation")}
          label={t("confirmDocument")}
        />
      </div>

      <div className="o-column u-margin--top_200">
        <SectionDivider
          children={
            <div className="o-row o-row--gap-size_100">
              <p className="u-margin_0 u-padding_0">{t("bankPledgePart1")}</p>
              <p className="neutral-value u-margin_0 u-padding_0 t-text-mini">
                {t("bankPledgePart2")}
              </p>
            </div>
          }
          noMargin={true}
        />
        <FilesUpload
          file={files.BankPledge}
          onFileChange={(file) => onFileChange(file, "BankPledge")}
          onDeleteFile={() => onDeleteFile("BankPledge")}
          label={t("bankPledge")}
        />
      </div>
    </div>
  );
}

function MansurBankFiles({ files, onFileChange, onDeleteFile }) {
  const { t } = useTranslation("general");
  return (
    <div className="o-column o-column--gap-size_200">
      <div>
        <SectionDivider title={t("transactionRequest")} noMargin={true} />
        <FileUpload
          fieldName="MansurTransactionRequest"
          file={files.MansurTransactionRequest}
          onFileChange={onFileChange}
          onDeleteFile={onDeleteFile}
        />
      </div>

      <div>
        <SectionDivider
          title={t("foreignCurrencyTradingWindowRequest")}
          noMargin={true}
        />
        <FileUpload
          fieldName="MansurBankForeignCurrencyTradingWindowRequest"
          file={files.MansurBankForeignCurrencyTradingWindowRequest}
          onFileChange={onFileChange}
          onDeleteFile={onDeleteFile}
        />
      </div>

      <div>
        <SectionDivider title={t("promise")} noMargin={true} />
        <FileUpload
          fieldName="MansurBankPromise"
          file={files.MansurBankPromise}
          onFileChange={onFileChange}
          onDeleteFile={onDeleteFile}
        />
      </div>
    </div>
  );
}

const FileUpload = ({ fieldName, file, onFileChange, onDeleteFile }) => {
  const fileInputRef = useRef(null);
  const { t } = useTranslation("general");

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="o-column o-column--gap-size_100 u-padding_0">
      <div className="u-width--max-width_300">
        <input
          ref={fileInputRef}
          type="file"
          id={fieldName}
          accept=".pdf"
          onChange={(e) => {
            const selectedFile = e.target.files?.[0];
            if (!selectedFile) return;

            const maxSize = 2 * 1024 * 1024;

            if (selectedFile.type !== "application/pdf") {
              alert(t("errorPdfType"));
              e.target.value = "";
              return;
            }

            if (selectedFile.size > maxSize) {
              alert(t("errorPdfSize"));
              e.target.value = "";
              return;
            }

            onFileChange(selectedFile, fieldName);
          }}
          className={`${classes3.fileInput} u-padding_0`}
          style={{ display: "none" }}
        />
        <div className="u-margin--top_300">
          <CustomButton
            flex
            title={t("uploadDocument")}
            icon={uploadIcon}
            onClick={handleButtonClick}
            iconSize={18}
            disabled={file}
          />
        </div>
        <div className="u-width--max-width_300 o-row o-row--vertical-align_center u-margin--top_300">
          <div className="o-row o-row--vertical-align_center">
            {file ? (
              <span className="">
                <DoneIcon width={30} height={30} />
              </span>
            ) : (
              fieldName != "BankPledge" && (
                <span className="u-color_success u-font-size_small o-row o-row--vertical-align_center o-row--gap-size_200">
                  <WarningIcon width={20} height={20} />
                  {t("noFileSelected")}
                </span>
              )
            )}
          </div>
          {file && (
            <div className={classes3.fileName}>
              {t("file")}
              <button
                className={classes3.deleteFileButton}
                onClick={() => onDeleteFile(fieldName)}
              >
                <img
                  src={deleteIcon}
                  className="u-height-specific_20"
                  alt="Delete"
                />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewInvoicePaymentsPage;
